<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3'
import InternalLayout from '@/layouts/InternalLayout.vue'
import AddressQuoteForm from '@/components/AddressQuoteForm.vue'
</script>

<template>
  <InternalLayout title="Pricing" hero-image="/img/professional-backyard-lawn-care.jpg">
    <Head title="Pricing">
      <meta head-key="description" name="description" content="Mow Managers offers simple, transparent lawn mowing pricing based on your actual turf size. Get a free instant price quote in seconds." />
      <link rel="canonical" href="https://mowmanagers.com/pricing" />
      <meta property="og:title" content="Pricing - Mow Managers" />
      <meta property="og:description" content="Simple, transparent lawn mowing pricing based on your actual turf size. Get a free instant price quote." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://mowmanagers.com/pricing" />
    </Head>

    <!-- ==================== FAIR PRICING INTRO ==================== -->
    <section aria-labelledby="pricing-heading" class="py-20 lg:py-28 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p class="text-mm-green text-[21px] font-extrabold uppercase tracking-[4.2px]">
          Simple & Transparent
        </p>
        <h2 id="pricing-heading" class="text-mm-dark text-3xl sm:text-4xl lg:text-[70px] font-bold lg:leading-[80px] mt-3">
          Fair Pricing For All,<br />Based on Actual Turf
        </h2>
        <p class="text-[#6d706d] text-[20px] font-medium mt-6 max-w-[720px] mx-auto leading-[34px]">
          When you enter your address and ZIP code on the pricing tab, our system will analyze your property and adjust the price based on your turf size. If you are satisfied with the results, you may proceed with submitting your order. We will review and you are good to go unless you hear from us.
        </p>
        <Link
          href="/get-started"
          class="inline-block mt-8 border-2 border-mm-green text-mm-green font-bold uppercase tracking-[1.6px] px-10 py-3 rounded-md hover:bg-mm-green hover:text-white transition-colors text-[16px] cursor-pointer"
        >
          Get My Price
        </Link>
      </div>
    </section>

    <!-- ==================== WE MAKE IT EASY — TWO COLUMN ==================== -->
    <section aria-labelledby="easy-heading" class="bg-mm-dark py-20 lg:py-28">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
          <div>
            <p class="text-mm-green text-[21px] font-extrabold uppercase tracking-[4.2px]">
              Quick & Easy
            </p>
            <h2 id="easy-heading" class="text-white text-3xl sm:text-4xl lg:text-[70px] font-bold lg:leading-[80px] mt-3">
              We Make<br />It Easy
            </h2>
            <p class="text-white text-[20px] font-medium mt-6 leading-[34px] max-w-[416px]">
              Just put in your address & zip code, that's all we need for you to customize your quote.
            </p>
            <div class="flex items-start gap-3 mt-6">
              <div class="w-[4px] bg-mm-green self-stretch rounded-full shrink-0" aria-hidden="true" />
              <p class="text-white text-[20px] font-medium leading-[34px]">
                Get a free instant price quote<br />in just seconds.
              </p>
            </div>
          </div>

          <div class="bg-white rounded-[20px] p-8 lg:p-14">
            <h3 id="quote-form-heading" class="text-mm-dark text-[26px] lg:text-[30px] font-bold text-center">
              Get a Free Instant<br />Price Quote.
            </h3>
            <div class="mt-8">
              <AddressQuoteForm variant="card" button-text="Submit" input-id-prefix="pricing" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ==================== QUALITY SERVICE + IMAGE ==================== -->
    <section aria-labelledby="quality-heading" class="pt-20 lg:pt-28 pb-20 lg:pb-28 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div class="rounded-[20px] overflow-hidden">
            <img
              src="/img/below-header-middle.webp"
              alt="Family enjoying a freshly mowed lawn"
              class="w-full h-full object-cover"
              width="640"
              height="480"
              loading="lazy"
            />
          </div>

          <div>
            <p class="text-mm-green text-[21px] font-extrabold uppercase tracking-[4.2px]">
              The Budget Way to Mow Your Lawn
            </p>
            <h2 id="quality-heading" class="text-mm-dark text-3xl sm:text-4xl lg:text-[70px] font-bold lg:leading-[88px] mt-3">
              Quality Service<br />and a Fair Price.
            </h2>
            <p class="text-[#6d706d] text-[20px] font-medium mt-6 leading-[34px] max-w-[628px]">
              Mow Managers connects you with trusted, local lawn care professionals at prices that won't break the bank. Get the quality you deserve with the convenience you need.
            </p>
            <Link
              href="/get-started"
              class="inline-block mt-8 bg-mm-green text-white font-bold uppercase tracking-[1.6px] px-8 py-3 rounded-md hover:bg-mm-green-dark transition-colors text-[16px] cursor-pointer"
            >
              Instant Quote
            </Link>
          </div>
        </div>
      </div>
    </section>
  </InternalLayout>
</template>
